import type { ApiCreateScanNote } from '@kaiizen/kai-pro-api';
import type { ScanDataMutators } from '@kaiizen/kai-pro-api-react-state';
import {
  getLatestProgressStatus,
  getScanPartUpdatesForProgressUpdate,
  type ScanPartOptimized,
  type ScanRootOptimized,
  type ScanTaskOptimized,
  type WorkStatus,
} from '@kaiizen/kai-scan-template-system';

/** Sidebar-friendly labels (match the CloseoutReviewFlow adapter's statusPresentation). */
export const WORK_STATUS_LABEL: Record<WorkStatus, string> = {
  notStarted: 'Not started',
  inProgress: 'In progress',
  needsRework: 'Needs rework',
  completed: 'Completed',
};

export type ProgressUpdateScanNoteMutators = {
  create: (record: Omit<ApiCreateScanNote, 'userId' | 'scanId'>) => Promise<unknown>;
};

export type BulkPhotoUploader = (
  filesOrFileList: File[] | FileList | null | undefined,
  scanPart: ScanPartOptimized | ScanRootOptimized,
  scanPartIds: { groupId?: string; taskId?: string; optionId?: string; lineItemId?: string },
) => Promise<unknown>;

export type ProgressUpdatePostSummary = {
  selectedStatus?: WorkStatus;
  photoCount: number;
  hasNote: boolean;
};

export const formatProgressUpdateSuccessToast = ({
  selectedStatus,
  photoCount,
  hasNote,
}: ProgressUpdatePostSummary): string => {
  const bits = ['Update posted'];
  if (selectedStatus) bits.push(WORK_STATUS_LABEL[selectedStatus]);
  if (photoCount > 0) bits.push(`${photoCount} photo${photoCount === 1 ? '' : 's'}`);
  if (hasNote) bits.push('note');
  return bits.join(' · ');
};

/** Posts a task progress update — status, note, and photos in one user action. A rework's note is its Instruction Note. */
export const submitTaskProgressUpdate = async ({
  task,
  groupId,
  selectedStatus,
  note,
  photoFiles,
  scanDataMutators,
  scanNoteMutators,
  userId,
  uploadPhotos,
}: {
  task: ScanTaskOptimized;
  groupId: string;
  selectedStatus?: WorkStatus;
  note: string;
  photoFiles: File[];
  scanDataMutators: ScanDataMutators;
  scanNoteMutators: ProgressUpdateScanNoteMutators;
  userId?: string;
  uploadPhotos: BulkPhotoUploader;
}): Promise<ProgressUpdatePostSummary> => {
  const trimmedNote = note.trim();
  const currentStatus = getLatestProgressStatus(task);
  const statusChanged = !!selectedStatus && selectedStatus !== currentStatus;

  if (statusChanged && selectedStatus) {
    scanDataMutators.updateScanPart(
      { groupId, taskId: task.id },
      getScanPartUpdatesForProgressUpdate(task, {
        status: selectedStatus,
        updatedAt: new Date(),
        updatedByUserId: userId,
      }),
    );
  }

  // A progress update's note is instructions for whoever does the work, so it is always external.
  if (trimmedNote) {
    await scanNoteMutators.create({
      scanPartId: task.id,
      scanNoteType: 'instructions',
      body: trimmedNote,
      visibility: 'external',
    });
  }

  if (photoFiles.length > 0) {
    await uploadPhotos(photoFiles, task, { groupId, taskId: task.id });
  }

  return {
    selectedStatus: statusChanged ? selectedStatus : undefined,
    photoCount: photoFiles.length,
    hasNote: !!trimmedNote,
  };
};
